import type { FilterAction } from '~/_shared/types/filter/filter';
import {
  isDriveTimePolygon, isGroupRadius, isIndividualRadius, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import { type SpreadsheetColumn } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import type { TranslationFnc } from '~/_shared/utils/hooks';
import { getDefaultOrCustomRouteName } from '~/directions/panel/directionsPanel.helpers';
import { getHeatMapItemName } from '~/heatMap/listing/heatMapListing.helpers';
import { getDriveTimePolygonShortLabel } from '~/proximity/proximity.helpers';
import { createTextPredicateFromFilterAction } from '~/sidebar/sidebarApps/mapTools/filterTool/filters/textFilter/textFilter.factory';
import { renderTextFilterPredicate } from '~/sidebar/sidebarApps/mapTools/filterTool/filters/textFilter/textFilterPredicate.enum';
import { mapToolIconMap } from '~/sidebar/sidebarApps/mapTools/mapTools.helpers';
import { getPlaceSearchName } from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.helpers';
import { type HeatMapItem } from '~/store/mapSettings/heatmaps/useHeatmapItems.selector';
import type { PlacesSearch } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import {
  type MapStatusBarItem, MapStatusBarItemRemoveType,
} from '../mapStatusBarItem.type';

export const createMapStatusBarItemFromGroupingColumn = ({ columnName, hierarchyIndicator, totalGroupingColumns, t, onRemove, onClick }: {
  columnName: string;
  hierarchyIndicator: number;
  totalGroupingColumns: number;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
},
): MapStatusBarItem => {
  const isPrimaryOrSecondarySuffix = totalGroupingColumns > 1
    ? ' (' + (hierarchyIndicator === 0 ? t('Primary Group') : t('Secondary Group')) + ')'
    : '';
  return {
    label: t('Grouping Column: {{columnName}}', {
      columnName,
    }) + isPrimaryOrSecondarySuffix,
    removeType: null,
    onRemove,
    onClick,
  };
};

export const createMapStatusBarItemFromActiveGroupingColumnFilter = ({ groupName, t, onRemove, onClick }: {
  groupName: string;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
},
): MapStatusBarItem => {
  return {
    label: t('Selected Group: {{groupName}}', {
      groupName,
    }),
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.DeletePermanently,
  };
};

export const createMapStatusBarItemFromGroupFilter = ({ columnName, selectedGroupName, t, onRemove, onClick }: {
  columnName: string;
  selectedGroupName: string;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
},
): MapStatusBarItem => {
  return {
    label: t('Selected Filter: {{columnName}}>{{selectedGroupName}}', {
      selectedGroupName,
      columnName,
    }),
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.DeletePermanently,
  };
};

export const createMapStatusBarItemFromNumberFilter = ({ columnName, selectedGroupName, t, onRemove, onClick }: {
  columnName: string;
  selectedGroupName: string;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
},
): MapStatusBarItem => {
  return {
    label: t('Selected Filter: {{columnName}}>{{selectedGroupName}}', {
      selectedGroupName,
      columnName,
    }),
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.DeletePermanently,
  };
};

export const createMapStatusBarItemFromTextFilter = ({ columnName, filterAction, text, t, onRemove, onClick }: {
  columnName: string;
  filterAction: FilterAction;
  text: string | null;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
},
): MapStatusBarItem => {
  const textPredicate = createTextPredicateFromFilterAction(filterAction);

  const translatedFilterAction = renderTextFilterPredicate(textPredicate, t);
  let label;

  if (text) {
    label = t('Selected Filter: {{columnName}}>{{filterAction}}>{{text}}', {
      columnName,
      filterAction: translatedFilterAction,
      text,
    });
  }
  else {
    label = t('Selected Filter: {{columnName}}>{{filterAction}}', {
      columnName,
      filterAction: translatedFilterAction,
      text,
    });
  }

  return {
    label,
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.DeletePermanently,
  };
};

export const createMapStatusBarFromProximity = ({ proximity, isProximityVisible, index, t, onRemove, onVisibilityChange, onClick }: {
  proximity: Proximity;
  isProximityVisible: boolean;
  index: number;
  t: TranslationFnc;
  onRemove: () => void;
  onVisibilityChange:
  (visibility: boolean) => void;
  onClick: () => void;
}): MapStatusBarItem => {
  let label = '';

  if (isDriveTimePolygon(proximity)) {
    label = t('Proximity: {{index}}. {{name}}, {{time}}', {
      index,
      name: proximity.name,
      time: getDriveTimePolygonShortLabel(proximity.data.hours, proximity.data.minutes),
    });
  }

  if (isGroupRadius(proximity) || isIndividualRadius(proximity)) {
    label = t('Proximity: {{index}}. {{name}}, {{distance}}{{unit}}', {
      index,
      name: isGroupRadius(proximity) ? `${t('Group').toUpperCase()}: ${proximity.name}` : proximity.name,
      distance: proximity.data.radius,
      unit: t(proximity.data.unit),
    });
  }

  return {
    label,
    isVisible: isProximityVisible,
    onVisibilityChange,
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.Undo,
  };
};

export const createMapStatusBarItemFromRoute = ({ routeName, t, onRemove, onClick, isVisible, onVisibilityChange }: {
  isVisible: boolean;
  routeName: string;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
  onVisibilityChange: (isVisible: boolean) => void;
}): MapStatusBarItem => {
  return {
    isVisible,
    label: getDefaultOrCustomRouteName(routeName, t),
    onRemove,
    onClick,
    onVisibilityChange,
    removeType: MapStatusBarItemRemoveType.Undo,
  };
};

export const createMapStatusBarItemFromHeatMap = ({ item, columns, t, onRemove, onVisibilityChange, onClick }: {
  item: HeatMapItem;
  columns: SpreadsheetColumn[];
  t: TranslationFnc;
  onRemove: () => void;
  onVisibilityChange: (visibility: boolean) => void;
  onClick: () => void;
}): MapStatusBarItem => {
  let heatMapItemName = getHeatMapItemName(item.heatmap, columns, t);
  if (heatMapItemName) {
    heatMapItemName = heatMapItemName.replace(':', ' >');
  }

  heatMapItemName = t('Heat Map: {{heatMapName}}', {
    heatMapName: heatMapItemName,
  });

  return {
    label: heatMapItemName,
    isVisible: item.otherOptions.visible,
    onVisibilityChange,
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.Undo,
  };
};

export const createMapStatusBarItemForHeatMapMarkerFilter = ({ item, columns, t, onRemove, onClick }: {
  item: HeatMapItem;
  columns: SpreadsheetColumn[];
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
}): MapStatusBarItem => {
  let heatMapItemName = getHeatMapItemName(item.heatmap, columns, t);
  if (heatMapItemName) {
    heatMapItemName = heatMapItemName.replace(':', ' >');
  }

  heatMapItemName = t('Show Markers For Heat Map: {{heatMapName}}', {
    heatMapName: heatMapItemName,
  });

  return {
    label: heatMapItemName,
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.DeletePermanently,
  };
};

export const createMapStatusBarItemFromBoundaryTerritoryGroupName = ({
  isCustom,
  isVisible,
  onClick,
  onRemove,
  onShowAllChange,
  t,
  territoryGroupName,
}: {
  isCustom: boolean;
  isVisible: boolean;
  onClick: () => void;
  onRemove: () => void;
  onShowAllChange: (showAll: boolean) => void;
  t: TranslationFnc;
  territoryGroupName: string;
}): MapStatusBarItem => {
  let label;
  if (isCustom) {
    label = t('Territory Group: {{territoryGroupName}}', { territoryGroupName });
  }
  else {
    label = t('Boundary: {{boundaryName}}', {
      boundaryName: territoryGroupName,
    });
  }

  return {
    label,
    onRemove,
    isVisible,
    onVisibilityChange: onShowAllChange,
    onClick,
    removeType: MapStatusBarItemRemoveType.RemoveFromMap,
  };
};

export const createMapStatusBarItemForIsolatePolygons = ({ name, t, onRemove, onClick }: {
  name: string;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
}): MapStatusBarItem => {
  return {
    label: t('Isolated: {{name}}', { name }),
    onRemove,
    onClick,
    removeType: MapStatusBarItemRemoveType.DeletePermanently,
  };
};

export const createMapStatusBarItemFromPlaceSearch = ({
  search, t, onRemove, onClick, isVisible, onVisibilityChange,
}: {
  search: PlacesSearch;
  isVisible: boolean;
  t: TranslationFnc;
  onRemove: () => void;
  onClick: () => void;
  onVisibilityChange: (isVisible: boolean) => void;
}): MapStatusBarItem => {
  return {
    icon: mapToolIconMap[MapTool.Places],
    isVisible,
    label: getPlaceSearchName(search, t),
    onRemove,
    onClick,
    onVisibilityChange,
    removeType: null,
  };
};
